/**
 * <hy-facets>: Verbesserung für Filter, Sortierung und Paginierung —
 * verwendet von sections/hy-main-collection.liquid (Kategorieseite) und
 * sections/hy-main-search.liquid (Suchseite, M10 AP-169). Reine
 * Erweiterung — ohne dieses Modul funktioniert alles über klassische
 * GET-Formular-Submits und echte Links (AP-74). Mit diesem Modul tauscht
 * jede Änderung nur das Filterformular (Tauschziel 1, #HyFacets-…) und das
 * Ergebnis-Fragment (Tauschziel 2) über die Section Rendering API aus,
 * ohne vollen Seitenneuladen.
 *
 * Nicht an die Kategorieseite gekoppelt (M10 AP-169): Die ID des
 * Ergebnis-Fragments kommt aus data-results-id auf dem Element (Fallback
 * HyCollectionResults-<sectionId> für Forks mit noch nicht aktualisierter
 * Sektion). Trefferzahl und Live-Region werden im getauschten Fragment
 * über [data-hy-facets-count] / [data-hy-facets-status] gefunden, nicht
 * über sektionsspezifische Klassennamen.
 *
 * Mobile Off-Canvas-Schublade = modal in der Darstellung (M10 AP-172b):
 * Unter 64rem ist .hy-facets ein seitendeckendes Overlay (position: fixed;
 * inset: 0), das Header und Toolbar mit abdeckt. Anders als die
 * Nav-Disclosure (hy-header-nav.js, hängt unter der Bar) gehört hier ALLES
 * außerhalb des Filterformulars inert — #inertOutside setzt das beim Öffnen
 * entlang der Ahnenkette, #restoreInert nimmt es beim Schließen exakt für
 * die selbst gesetzten Elemente zurück. Anfangsfokus auf .hy-facets__close.
 * Kein natives <dialog>/showModal(): dasselbe <hy-facets>-Element trägt ab
 * 64rem die statische Desktop-Sidebar (kein Overlay) + die AJAX-Swap-Logik.
 * role="dialog"/aria-modal/aria-label nur im offenen Zustand (Label aus
 * data-hy-dialog-label, Liquid-übersetzt). #desktopQuery erzwingt zusätzlich
 * zu Escape das Schließen, wenn der Viewport bei offener Schublade über
 * 64rem wechselt (reviewer-Fund PR F2, sonst bliebe inert ohne sichtbaren
 * Schließen-Weg hängen).
 *
 * Zwei getrennte AbortController: #controller für den Lebenszyklus
 * (Projektmuster, siehe hy-header-nav.js), #fetchController pro Anfrage
 * — ein Abbruch des einen darf den anderen nicht mitreißen, sonst würde
 * jeder neue Request auch alle Listener abreißen.
 *
 * Schlägt irgendein Schritt fehl (Netzwerk, unerwarteter Status, ein
 * erwartetes Element fehlt in der Antwort), navigiert das Modul ganz
 * normal — Formular-Submit, echte Link-Navigation oder
 * window.location.reload() bei einem gescheiterten Zurück-Button. Es
 * gibt keinen Zustand, in dem eine Aktion nichts tut.
 */
class HyFacets extends HTMLElement {
  #controller = null;
  #fetchController = null;
  #debounceTimer = null;
  #inertedElements = [];
  #desktopQuery = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.setAttribute('data-hy-facets-ready', '');

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    window.addEventListener('popstate', this.#handlePopState, { signal });

    // reviewer-Fund PR F2: Bleibt die Schublade offen (data-hy-facets-open +
    // #inertedElements), während der Viewport über 64rem wechselt (Rotation,
    // Fenster-Resize), verschwindet das Overlay per CSS in die Desktop-
    // Sidebar zurück, aber Toolbar/Ergebnis-Fragment blieben ohne sichtbaren
    // Schließen-Weg inert — Escape ist der einzige (viewport-unabhängige)
    // Recovery-Pfad. Schließt hier zusätzlich zwangsweise, sobald der
    // Breakpoint überschritten wird.
    this.#desktopQuery = window.matchMedia('(min-width: 64rem)');
    this.#desktopQuery.addEventListener(
      'change',
      (event) => {
        if (event.matches && this.hasAttribute('data-hy-facets-open')) this.#setDrawer(false);
      },
      { signal },
    );
  }

  disconnectedCallback() {
    // Falls die Schublade gerade offen war (z. B. ein Theme-Editor-
    // Section-Re-Render während der mobilen Vorschau), muss die
    // Scroll-Sperre mit heruntergehen — sonst bleibt document.
    // documentElement dauerhaft overflow: hidden, weit über das Leben
    // dieses Elements hinaus (QS-Fund, PR-#5-Abnahme).
    if (this.hasAttribute('data-hy-facets-open')) this.#setDrawer(false);

    this.#controller?.abort();
    this.#fetchController?.abort();
    clearTimeout(this.#debounceTimer);
  }

  get #formId() {
    return `HyFacets-${this.dataset.sectionId}`;
  }

  get #resultsId() {
    return this.dataset.resultsId || `HyCollectionResults-${this.dataset.sectionId}`;
  }

  #handleClick = (event) => {
    if (event.target.closest('.hy-facets__toggle')) {
      this.#setDrawer(!this.hasAttribute('data-hy-facets-open'));
      return;
    }

    if (event.target.closest('.hy-facets__close')) {
      this.#setDrawer(false);
      return;
    }

    // [href] schließt die Preisfilter-Chips aus (span, kein a — url_to_remove
    // liefert für price_range einen leeren String, bestätigter
    // Plattform-Befund, siehe docs/sections.md beim Preisfilter-Eintrag).
    const chip = event.target.closest('.hy-facet-chips__chip[href]');
    if (chip) {
      event.preventDefault();
      this.#navigate(chip.href, { fallbackHref: chip.href });
      return;
    }

    const clear = event.target.closest('.hy-facets__clear');
    if (clear) {
      event.preventDefault();
      this.#navigate(clear.href, { fallbackHref: clear.href });
      return;
    }

    const paginationLink = event.target.closest(
      '.hy-pagination__link, .hy-pagination__prev, .hy-pagination__next',
    );
    if (paginationLink) {
      event.preventDefault();
      this.#navigate(paginationLink.href, { fallbackHref: paginationLink.href });
    }
  };

  #handleChange = (event) => {
    const form = event.target.form;
    if (!form || form.id !== this.#formId) return;

    // Trailing Debounce ~250ms: fünf schnell angeklickte Checkboxen ergeben
    // eine Anfrage statt fünf (R11-Gegenmaßnahme, M5-Plan §3.4). Der Timer
    // wird nicht hier, sondern zentral in #navigate() gelöscht — jeder
    // andere Navigationsweg (Chip, Paginierung, Zurücksetzen, popstate)
    // läuft ebenfalls durch #navigate() und muss einen noch schwebenden
    // Debounce genauso verwerfen, sonst überholt ein 250ms alter,
    // "vergessener" Filterwechsel die eigentlich gerade erfolgte
    // Navigation (QS-Fund, PR-#5-Abnahme: betraf insbesondere den
    // Zurück-Button, dessen bewusst unterlassenes pushState ein
    // nachlaufender Debounce-Call sonst doch ausgelöst hätte).
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => {
      this.#submitForm(form);
    }, 250);
  };

  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== this.#formId) return;

    event.preventDefault();
    this.#submitForm(form);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.hasAttribute('data-hy-facets-open')) return;
    this.#setDrawer(false);
  };

  #handlePopState = () => {
    // Kein State-Guard: feuert auch für den Ur-Eintrag (state === null),
    // der zurück in den ungefilterten Zustand führen muss.
    this.#navigate(window.location.href, { isPopState: true });
  };

  #submitForm(form) {
    // Ziel-URL exakt so gebaut, wie das Formular sie ohne JS bauen würde
    // (M5-Plan §3.4) — teilbar, refresh-fest, identisch zum No-JS-Pfad.
    const url = new URL(form.action);
    const params = new URLSearchParams(new FormData(form));
    for (const key of Array.from(params.keys())) {
      if (params.get(key) === '') params.delete(key);
    }
    url.search = params.toString();
    this.#navigate(url.toString(), { formFallback: form });
  }

  #navigate(href, { fallbackHref, formFallback, isPopState } = {}) {
    // Zentraler Übergabepunkt für jeden Navigationsweg (Formular-Submit,
    // Chip/Paginierung/Zurücksetzen-Klick, popstate) — ein noch
    // schwebender Debounce aus #handleChange darf keinen dieser Wege
    // nachträglich überholen.
    clearTimeout(this.#debounceTimer);
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const fetchUrl = new URL(href, window.location.href);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

    this.setAttribute('aria-busy', 'true');

    fetch(fetchUrl.toString(), { signal: this.#fetchController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`hy-facets: status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        const responseDoc = new DOMParser().parseFromString(responseText, 'text/html');
        this.#swap(responseDoc, href, isPopState);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;

        if (isPopState) {
          window.location.reload();
          return;
        }
        if (formFallback) {
          formFallback.submit();
          return;
        }
        window.location.assign(fallbackHref || href);
      })
      .finally(() => {
        this.removeAttribute('aria-busy');
      });
  }

  #swap(responseDoc, url, isPopState) {
    const currentForm = document.getElementById(this.#formId);
    const currentResults = document.getElementById(this.#resultsId);
    const newForm = responseDoc.getElementById(this.#formId);
    const newResults = responseDoc.getElementById(this.#resultsId);
    if (!currentForm || !currentResults || !newForm || !newResults) {
      throw new Error('hy-facets: swap target missing');
    }

    currentForm.replaceWith(newForm);
    currentResults.replaceWith(newResults);

    // pushState, nicht replaceState (wie hy-variant-picker.js bei einem
    // Combined-Listing-Wechsel): ein Filter-/Sortier-/Seitenwechsel ist ein
    // neuer "Ort", keine bloße Formaktualisierung.
    if (!isPopState) {
      history.pushState({}, '', url);
    }

    this.#setDrawer(false);

    const countEl = newResults.querySelector('[data-hy-facets-count]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    countEl?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    countEl?.focus();

    // Übernimmt den bereits serverseitig übersetzten Trefferzahl-Text in
    // die Live-Region statt ihn hier neu zu bauen — CLAUDE.md: Übersetzungen
    // gehören nicht ins JavaScript.
    const statusEl = newResults.querySelector('[data-hy-facets-status]');
    if (statusEl && countEl) statusEl.textContent = countEl.textContent.trim();

    document.dispatchEvent(
      new CustomEvent('hy:filter:updated', { detail: { sectionId: this.dataset.sectionId } }),
    );
  }

  #setDrawer(open) {
    this.toggleAttribute('data-hy-facets-open', open);
    this.querySelector('.hy-facets__toggle')?.setAttribute('aria-expanded', String(open));

    // Fokus-Containment (M10 AP-172b): Unter 64rem ist die Schublade ein
    // seitendeckendes Overlay (position: fixed; inset: 0; z-index: overlay,
    // siehe snippets/hy-facets.liquid) — modal in der Darstellung, es deckt
    // Header und Toolbar mit ab. Anders als die Nav-Disclosure (die unter der
    // Bar hängt, hy-header-nav.js) gehört hier ALLES außerhalb von <hy-facets>
    // inert, damit Tab nur durch die Schublade läuft. Kein natives <dialog>/
    // showModal(): dasselbe <hy-facets>-Element trägt ab 64rem die statische
    // Desktop-Sidebar (kein Overlay) + die AJAX-Swap-Logik — showModal() würde
    // beides bekämpfen.
    if (open) this.#inertOutside();
    else this.#restoreInert();

    // ARIA-Dialog-Semantik nur im offenen (mobilen) Zustand — auf Desktop
    // bleibt dasselbe <form> die Sidebar ohne Dialog-Rolle. Label kommt aus
    // Liquid (data-hy-dialog-label), nicht hartkodiert im JS.
    const form = this.querySelector('.hy-facets');
    if (form) {
      if (open) {
        form.setAttribute('role', 'dialog');
        form.setAttribute('aria-modal', 'true');
        if (form.dataset.hyDialogLabel) form.setAttribute('aria-label', form.dataset.hyDialogLabel);
      } else {
        form.removeAttribute('role');
        form.removeAttribute('aria-modal');
        form.removeAttribute('aria-label');
      }
    }

    // Wie assets/hy-product-gallery.js beim Zoom-Dialog: nötig, weil die
    // Schublade kein natives <dialog>/showModal() ist (M5-Plan §3.5), das
    // echtes Hintergrund-Scrollen von sich aus sperren würde.
    document.documentElement.style.overflow = open ? 'hidden' : '';

    if (open) {
      // WAI-ARIA-APG „Dialog (Modal)": Fokus in die Schublade beim Öffnen.
      this.querySelector('.hy-facets__close')?.focus();
    } else {
      this.querySelector('.hy-facets__toggle')?.focus();
    }
  }

  // Setzt inert auf alles außerhalb des Filterformulars (.hy-facets):
  // beginnt bei der Form und läuft die Ahnenkette bis <body> hoch, auf jeder
  // Ebene die Geschwister des aktuellen Knotens. Damit werden auch die
  // Geschwister INNERHALB von <hy-facets> (Toolbar, Ergebnis-Fragment)
  // erreicht, die hinter dem Overlay in Fluss bleiben. Macht keine Annahme
  // über die DOM-Form (fork-sicher). Merkt sich nur die selbst gesetzten
  // Elemente, damit #restoreInert kein inert entfernt, das ein anderes Modul
  // (z. B. hy-header-nav.js auf #MainContent) gesetzt hat.
  #inertOutside() {
    this.#restoreInert();
    const form = this.querySelector('.hy-facets');
    if (!form) return;
    for (let node = form; node && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement?.children ?? []) {
        if (sibling === node || sibling.hasAttribute('inert')) continue;
        sibling.setAttribute('inert', '');
        this.#inertedElements.push(sibling);
      }
    }
  }

  #restoreInert() {
    for (const el of this.#inertedElements) el.removeAttribute('inert');
    this.#inertedElements = [];
  }
}

customElements.define('hy-facets', HyFacets);
